import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    getAbsolutePagePathInRevision,
    isRevisionPageDocument,
    isRevisionPageLink,
    isRevisionPageParent,
    type Revision,
    type RevisionPage,
    RevisionPageType,
    type RevisionTreePage,
} from '@gitbook/hive-core';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonGroup,
    ButtonSkeleton,
    Checkbox,
    Disclosure,
    DisclosureChevron,
    DisclosureTrigger,
    Heading,
    Menu,
    MenuItem,
    MenuRoot,
    MenuTrigger,
    Popover,
} from '@gitbook/spine-react';
import type { DiffTreeChange } from '@gitbook/util-tree-merge';

import { useNavigation } from '../../../navigation';
import { useAssertCurrentOrganizationId } from '../../../state';
import {
    CommentButton,
    CommentsScopedProvider,
    PageDiffIndicator,
    useContentReference,
    useSiteContentPreviewURL,
} from '../..';
import {
    PageDocumentDiff,
    type PageDocumentDiffProps,
    PageDocumentDiffSkeleton,
} from './PageDocumentDiff';
import { useViewedPageChange } from './viewedChanges';

export type PageDiffPanelProps = {
    isExpanded: boolean;
    onExpandedChange: (isExpanded: boolean) => void;
} & PageDocumentDiffProps;

/**
 * Expandable panel to display page changes.
 */
export const PageDiffPanel = React.memo(function PageDiffPanel(props: PageDiffPanelProps) {
    const { isExpanded, onExpandedChange, changeRequest, pageDiff, baseRevision, currentRevision } =
        props;
    const { page } = pageDiff.item;
    if (isRevisionPageLink(page)) {
        return (
            <PanelDiffHeader isExpanded={false}>
                <div className="flex items-center gap-1.5">
                    <PageDiffHeaderTitle
                        baseRevision={baseRevision}
                        currentRevision={currentRevision}
                        pageDiff={pageDiff}
                    />
                    <React.Suspense
                        fallback={
                            <ButtonSkeleton
                                label="Link to page"
                                iconOnly
                                trailingIcon={Icon.PageLink}
                                size="small"
                            />
                        }
                    >
                        <PageLinkButton contentRef={page.target as api.ContentRef} />
                    </React.Suspense>
                </div>
            </PanelDiffHeader>
        );
    }
    return (
        <CommentsScopedProvider pageId={pageDiff.item.page.id}>
            <Disclosure isExpanded={isExpanded} onExpandedChange={onExpandedChange}>
                <div>
                    <PanelDiffHeader isExpanded={isExpanded}>
                        <DisclosureTrigger>
                            <Button
                                leadingIcon={<DisclosureChevron />}
                                iconOnly
                                size="small"
                                label="Toggle page"
                                kind="nested"
                            />
                        </DisclosureTrigger>

                        <PageDiffHeaderTitle
                            baseRevision={baseRevision}
                            currentRevision={currentRevision}
                            pageDiff={pageDiff}
                        />

                        <ButtonGroup>
                            <ViewedToggle
                                changeRequestId={changeRequest.id}
                                page={page}
                                onExpandedChange={onExpandedChange}
                            />

                            {isRevisionPageDocument(page) ? (
                                <>
                                    <CommentButton
                                        kind="nested"
                                        size="medium"
                                        label="Comment on this page"
                                        shortenLabel
                                        iconOnly
                                        leadingIcon={Icon.Comment}
                                        nodeKey={undefined}
                                        intent="create" // To ensure it always open as a popover
                                    />
                                    <MenuRoot>
                                        <MenuTrigger>
                                            <Button
                                                leadingIcon={Icon.MoreVertical}
                                                iconOnly
                                                label="Actions"
                                                kind="nested"
                                            />
                                        </MenuTrigger>
                                        <Popover>
                                            <PageActionsMenu
                                                changeRequest={changeRequest}
                                                currentRevision={currentRevision}
                                                page={page}
                                            />
                                        </Popover>
                                    </MenuRoot>
                                </>
                            ) : null}
                        </ButtonGroup>
                    </PanelDiffHeader>
                    {isExpanded ? (
                        <div className="rounded-b-lg border-x border-b">
                            <div className="px-12 py-4">
                                {isMovedWithoutChangePageDocument(pageDiff) ? (
                                    <p className="text-secondary text-ui-base">
                                        This page has been moved without any changes.
                                    </p>
                                ) : (
                                    <React.Suspense fallback={<PageDocumentDiffSkeleton />}>
                                        <PageDocumentDiff {...props} />
                                    </React.Suspense>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            </Disclosure>
        </CommentsScopedProvider>
    );
});

function PageActionsMenu(props: {
    changeRequest: api.ChangeRequest;
    page: RevisionPage;
    currentRevision: Revision;
}) {
    const { changeRequest, page, currentRevision } = props;
    const { navigate } = useNavigation();
    const pagePath = usePagePath(currentRevision, page.id);
    const organizationId = useAssertCurrentOrganizationId();
    const previewURL = useSiteContentPreviewURL({
        spaceId: changeRequest.space,
        organizationId,
        revisionId: currentRevision.id,
        changeRequestNumber: changeRequest?.number,
        pagePath: pagePath ?? '',
    });
    return (
        <Menu>
            {previewURL ? (
                <MenuItem
                    icon={Icon.Eye}
                    onAction={() => {
                        window.open(previewURL, '_blank');
                    }}
                >
                    Preview
                </MenuItem>
            ) : null}
            <MenuItem
                icon={Icon.Edit}
                onAction={() => {
                    navigate({
                        screen: {
                            type: 'space',
                            space: changeRequest.space,
                            changeRequest: changeRequest.number,
                            revision: undefined,
                            mode: 'edit',
                            page: pagePath || undefined,
                        },
                    });
                }}
            >
                Edit
            </MenuItem>
        </Menu>
    );
}

const PanelDiffHeader = React.forwardRef(function PanelDiffHeader(
    props: React.ComponentPropsWithoutRef<'div'> & {
        isExpanded: boolean;
    },
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const { isExpanded, children, ...rest } = props;
    return (
        <div
            ref={ref}
            {...rest}
            className={clsx(
                'sticky top-[var(--changes-header-height)] z-10 bg-base',
                rest.className
            )}
        >
            <div
                className={clsx(
                    'group flex w-full items-start gap-3 border bg-standout-base px-3 py-2 text-left shadow-sm backdrop-blur-sm focus-visible:shadow-focus-ring-inset',
                    isExpanded ? 'rounded-t-lg' : 'rounded-lg'
                )}
            >
                <div className="flex flex-1 items-center gap-2">{children}</div>
            </div>
        </div>
    );
});

type PageDiffHeaderTitleProps = {
    pageDiff: DiffTreeChange<RevisionTreePage>;
} & Omit<PageDiffPathSegmentProps, 'pageId'>;

function PageDiffHeaderTitle(props: PageDiffHeaderTitleProps) {
    const { pageDiff, currentRevision, baseRevision } = props;
    const page = pageDiff.item.page;
    const isPageLink = isRevisionPageLink(page);
    return (
        <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="flex flex-row items-center gap-2">
                <PageDiffIndicator pageDiff={pageDiff} />
                <Heading
                    level={3}
                    className="truncate text-secondary text-ui-action group-hover:text-base"
                >
                    {page.title}
                </Heading>
            </div>
            {isPageLink ? null : (
                <PageDiffPathSegment
                    pageId={page.id}
                    currentRevision={currentRevision}
                    baseRevision={baseRevision}
                />
            )}
        </div>
    );
}

type PageDiffPathSegmentProps = {
    pageId: string;
    currentRevision: Revision;
    baseRevision: Revision;
};

function PageDiffPathSegment(props: PageDiffPathSegmentProps) {
    const { pageId, currentRevision, baseRevision } = props;
    const currentPath = usePagePath(currentRevision, pageId);
    const basePath = usePagePath(baseRevision, pageId);
    if (!currentPath || basePath === currentPath) {
        return null;
    }
    return <PathSegment basePath={basePath} currentPath={currentPath} />;
}

function ViewedToggle(props: {
    changeRequestId: string;
    page: RevisionPage;
    onExpandedChange: (isExpanded: boolean) => void;
}) {
    const { changeRequestId, page, onExpandedChange } = props;
    const [viewed, setViewed] = useViewedPageChange({ changeRequestId, pageId: page.id });
    const documentId = isRevisionPageDocument(page) ? (page.document ?? null) : null;
    const isViewed = viewed && viewed.documentId === documentId;
    return (
        <Button
            label="Viewed"
            kind="nested"
            leadingIcon={<Checkbox value={isViewed} readOnly />}
            onPress={() => {
                const nextViewed = isViewed ? false : { documentId };
                setViewed(nextViewed);
                onExpandedChange(!nextViewed);
            }}
        />
    );
}

function PageLinkButton(props: { contentRef: api.ContentRef }) {
    const contentRef = useContentReference(props.contentRef);
    return (
        <Button
            onPress={() => contentRef.open()}
            label={`Link to ${contentRef.title}`}
            iconOnly
            trailingIcon={Icon.PageLink}
            kind="nested"
            size="small"
        />
    );
}

function usePagePath(revision: Revision, pageId: string) {
    return React.useMemo(() => {
        const basePage = revision.pages[pageId];
        if (!basePage || !isRevisionPageParent(basePage)) {
            return null;
        }

        return getAbsolutePagePathInRevision(revision, pageId);
    }, [revision, pageId]);
}

/**
 * Display a path segment in the page diff panel header.
 */
function PathSegment(props: { basePath: string | null; currentPath: string }) {
    const { basePath, currentPath } = props;
    return (
        <span className="inline-flex h-full max-w-full flex-row items-center gap-1 text-secondary text-ui-small">
            {basePath ? (
                <>
                    <span className="truncate rounded border border-base bg-muted px-1.5 py-0.5 content-mono">
                        /{basePath}
                    </span>
                    <Icon.ArrowRight className="shrink-0" />
                </>
            ) : null}
            <span className="truncate rounded border border-base bg-muted px-1.5 py-0.5 content-mono">
                /{currentPath}
            </span>
        </span>
    );
}

function isMovedWithoutChangePageDocument(pageDiff: DiffTreeChange<RevisionTreePage>) {
    if (pageDiff.type !== 'modified') {
        return false;
    }

    if (
        pageDiff.before.page.type !== RevisionPageType.Document ||
        pageDiff.item.page.type !== RevisionPageType.Document
    ) {
        return false;
    }

    return pageDiff.before.page.document === pageDiff.item.page.document;
}
